import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useChatWebSocket } from '../hooks/useChatWebSocket';
import { useAudioAssist } from '../hooks/useAudioAssist';
import { useI18n } from '../hooks/useI18n'; // Import the i18n hook
import MessageList from './MessageList';
import ChatInput from './ChatInput';
import AudioAssistToggle from './AudioAssistToggle';
import { ChatMessage } from '../types/chat';
import { useChatContext } from '../context/ChatContext';

import headerIllustration from '../assets/livy-illustration.jpg';

const ChatScreen: React.FC = () => {
  const t = useI18n();

  const initialMessages = useMemo<ChatMessage[]>(() => [], []);

  const { triggerHistoryRefresh } = useChatContext();

  const [inputValue, setInputValue] = useState('');
  const [initialScrollDone, setInitialScrollDone] = useState(false);
  const [displayMessages, setDisplayMessages] = useState<ChatMessage[]>([]);

  const scrollableContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const staticContentWrapperRef = useRef<HTMLDivElement>(null);

  const [searchParams] = useSearchParams();
  const rawUserQuery = (searchParams.get('userQuery') || '').trim();

  const inputRef = useRef<HTMLInputElement>(null);

  const handleSessionCreated = useCallback(
    (_sessionId: string) => {
      triggerHistoryRefresh.emit();
    },
    [triggerHistoryRefresh]
  );

  const { messages, isConnected, error, sendMessage, currentSessionId, isTyping } =
    useChatWebSocket(initialMessages, handleSessionCreated);

  const {
    isEnabled: audioEnabled,
    isSupported: audioSupported,
    currentlyPlayingId,
    isLoading: audioLoadingMessageId,
    cachedAudioCount,
    toggleAudioAssist,
    playMessageAudio,
    stopSpeech,
    clearAudioCache,
    isMessagePlaying,
    isMessageLoading,
    hasMessageCachedAudio,
    getMessageProgress,
  } = useAudioAssist();

  useEffect(() => {
    if (currentSessionId) {
      setTimeout(() => {
        triggerHistoryRefresh.emit();
      }, 500);
    }
  }, [currentSessionId, triggerHistoryRefresh]);

  useEffect(() => {}, [currentSessionId, rawUserQuery]);

  useEffect(() => {
    setDisplayMessages(messages);
  }, [messages]);

  const handleSend = () => {
    const trimmedInput = inputValue.trim();
    if (trimmedInput && isConnected) {
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        text: trimmedInput,
        timestamp: new Date().toISOString(),
      };

      setDisplayMessages(prev => [...prev, userMessage]);

      sendMessage(trimmedInput);
      setInputValue('');

      if (currentlyPlayingId) {
        stopSpeech();
      }

      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
    } else if (!isConnected) {
      console.warn('Attempted to send message while disconnected.');
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const scrollElement = scrollableContainerRef.current;
    const staticContentElement = staticContentWrapperRef.current;

    if (!scrollElement) return;

    if (displayMessages.length > 0 && !initialScrollDone && staticContentElement) {
      const staticContentHeight = staticContentElement.offsetHeight;
      scrollElement.scrollTop = staticContentHeight;
      setInitialScrollDone(true);
    } else if (initialScrollDone) {
      setTimeout(() => {
        scrollElement.scrollTo({ top: scrollElement.scrollHeight, behavior: 'smooth' });
      }, 0);
    }
  }, [displayMessages, initialScrollDone]);

  // Process messages with enhanced audio information
  const processedMessages = displayMessages.map(msg => ({
    ...msg,
    // Add audio-related properties with enhanced information
    isAudioEnabled: audioEnabled,
    isCurrentlyPlaying: isMessagePlaying(msg.id || ''),
    isAudioLoading: isMessageLoading(msg.id || ''),
    hasCachedAudio: hasMessageCachedAudio(msg.id || ''),
    audioProgress: getMessageProgress(msg.id || ''),
    onPlayAudio: (messageId: string, text: string) => playMessageAudio(messageId, text),
    onStopAudio: stopSpeech,
  }));

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      stopSpeech();
    };
  }, [stopSpeech]);

  return (
    <div className="flex flex-col w-full h-full fixed inset-0 bg-white">
      {/* Scrollable Content Area */}
      <div
        ref={scrollableContainerRef}
        className="flex-grow overflow-y-auto p-2 sm:p-3 md:p-4 pb-1"
      >
        <div ref={staticContentWrapperRef} className="mb-4">
          {/* Top Static Info Section */}
          <div className="text-center mb-4">
            <img
              src={headerIllustration}
              alt="Livy Assistant"
              className="block mx-auto max-w-[120px] sm:max-w-[150px] h-auto mb-3 sm:mb-4"
            />

            <h2 className="text-lg font-medium mb-1">{t.get('helloMessage')}</h2>

            <hr className="border-t-2 border-[#fdd835] my-4 max-w-xs mx-auto" />
            <p className="text-sm text-gray-600 mb-4 px-4">{t.get('questionPrompt')}</p>
          </div>

          {/* Settings Section */}
          <div className="px-2">
            <Accordion elevation={1} className="mb-2 shadow-sm rounded-2xl overflow-hidden">
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className="rounded-t-2xl">
                <p className="text-sm">{t.get('wordsOfAdviceTitle')}</p>
              </AccordionSummary>
              <AccordionDetails className="p-2 sm:p-4 rounded-b-2xl">
                <ul className="text-sm list-disc pl-5 space-y-1">
                  {t.getWordsOfAdvice().map((advice, index) => (
                    <li key={index}>{advice}</li>
                  ))}
                </ul>
              </AccordionDetails>
            </Accordion>

            {/* Enhanced Audio Assist Toggle */}
            <AudioAssistToggle
              isEnabled={audioEnabled}
              isSupported={audioSupported}
              hasPlayingMessage={!!currentlyPlayingId}
              onToggle={toggleAudioAssist}
              onStopAll={stopSpeech}
              onClearCache={clearAudioCache}
              cachedAudioCount={cachedAudioCount}
            />
          </div>
        </div>

        {/* Message List with Enhanced Audio Props */}
        <MessageList
          messages={processedMessages}
          sessionId={currentSessionId}
          // Pass enhanced audio-related props
          audioEnabled={audioEnabled}
          apiLoadingId={audioLoadingMessageId}
          currentlyPlayingId={currentlyPlayingId}
          onPlayAudio={playMessageAudio}
          onStopAudio={stopSpeech}
          isOverallTyping={isTyping}
        />

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area (Fixed at the bottom) */}
      <div className="flex-shrink-0">
        <ChatInput
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onSend={handleSend}
          disabled={!isConnected || isTyping}
          inputRef={inputRef}
          placeholder={t.get('chatPlaceholder')} // Pass translated placeholder
        />
        {error && (
          <p className="block text-center p-1 bg-white text-red-600 text-xs">
            Connection error: {error}
          </p>
        )}

        <p className="text-center text-gray-600 text-xs mt-1 mb-2 italic">{t.get('disclaimer')}</p>
      </div>
    </div>
  );
};

export default ChatScreen;
